// ============================================================
// Page Plans — Lot L05
// Liste + création + édition des plans SaaS (Standard / Premium / custom)
// ============================================================
import React, { useEffect, useState } from 'react';
import superAdminApi from '../../services/superAdminApi';
import { Plus, Tag, AlertCircle, ToggleRight, ToggleLeft, Pencil } from 'lucide-react';
import InputMontant from '../../components/common/InputMontant';
import toast from 'react-hot-toast';

function ModalPlan({ initial, onClose, onSuccess }) {
  const editMode = Boolean(initial?.id);
  const estEssai = initial?.est_essai === true || initial?.code === 'essai';
  const [form, setForm] = useState({
    code: initial?.code || '',
    nom: initial?.nom || '',
    description: initial?.description || '',
    prix_mensuel: initial?.prix_mensuel ?? 0,
    prix_annuel:  initial?.prix_annuel ?? 0,
    max_boutiques_default: initial?.max_boutiques_default ?? 1,
    max_utilisateurs_default: initial?.max_utilisateurs_default ?? 5,
    ordre: initial?.ordre ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        prix_mensuel: estEssai ? 0 : (parseInt(form.prix_mensuel, 10) || 0),
        prix_annuel:  estEssai ? 0 : (parseInt(form.prix_annuel, 10) || 0),
        max_boutiques_default: parseInt(form.max_boutiques_default, 10) || 1,
        max_utilisateurs_default: parseInt(form.max_utilisateurs_default, 10) || 5,
        ordre: parseInt(form.ordre, 10) || 0,
      };
      if (editMode) {
        delete payload.code;
        await superAdminApi.put(`/superadmin/plans/${initial.id}`, payload);
        toast.success('Plan mis à jour');
      } else {
        await superAdminApi.post('/superadmin/plans', payload);
        toast.success('Plan créé');
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-xl p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <h3 className="text-slate-900 font-semibold flex items-center gap-2">
          <Tag size={16} className="text-blue-600" /> {editMode ? 'Modifier le plan' : 'Nouveau plan'}
        </h3>

        <div className="grid grid-cols-2 gap-3">
          <div className={editMode ? 'col-span-2 opacity-60' : ''}>
            <label className="block text-xs text-slate-500 mb-1">Code <span className="text-red-600">*</span></label>
            <input name="code" value={form.code} onChange={handleChange}
              disabled={editMode}
              pattern="[a-z0-9_-]+" required
              placeholder="standard, premium, custom_xyz"
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm disabled:opacity-60 focus:outline-none focus:border-blue-600" />
            {!editMode && <p className="text-xs text-slate-400 mt-1">Identifiant technique, non modifiable après création (a-z, 0-9, -, _).</p>}
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Nom affiché <span className="text-red-600">*</span></label>
            <input name="nom" value={form.nom} onChange={handleChange} required
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={2}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div className={estEssai ? 'opacity-60' : ''}>
            <label className="block text-xs text-slate-500 mb-1">Prix mensuel (GNF)</label>
            <InputMontant
              value={estEssai ? 0 : form.prix_mensuel} onChange={v => setForm(f => ({ ...f, prix_mensuel: v }))} disabled={estEssai}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm disabled:cursor-not-allowed focus:outline-none focus:border-blue-600" />
          </div>
          <div className={estEssai ? 'opacity-60' : ''}>
            <label className="block text-xs text-slate-500 mb-1">Prix annuel (GNF)</label>
            <InputMontant
              value={estEssai ? 0 : form.prix_annuel} onChange={v => setForm(f => ({ ...f, prix_annuel: v }))} disabled={estEssai}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm disabled:cursor-not-allowed focus:outline-none focus:border-blue-600" />
          </div>
          {estEssai && (
            <p className="col-span-2 text-xs text-amber-600 -mt-1">
              Le plan d'essai est gratuit par définition : les prix sont verrouillés à 0.
            </p>
          )}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Boutiques max</label>
            <input name="max_boutiques_default" type="number" min="1" value={form.max_boutiques_default} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Utilisateurs max</label>
            <input name="max_utilisateurs_default" type="number" min="1" value={form.max_utilisateurs_default} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Ordre d'affichage</label>
            <input name="ordre" type="number" value={form.ordre} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
        </div>

        {error && <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {error}</div>}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            {saving ? '…' : editMode ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </form>
    </div>
  );
}

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

export default function Plans() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | {} pour création | { id, ... } pour édition
  const [showInactive, setShowInactive] = useState(false);

  const load = () => {
    setLoading(true);
    superAdminApi.get('/superadmin/plans')
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(e.response?.data?.message || 'Erreur chargement'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleActif = async (p) => {
    try {
      await superAdminApi.patch(`/superadmin/plans/${p.id}/statut`, { actif: !p.actif });
      toast.success(p.actif ? 'Plan désactivé' : 'Plan activé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  const actifs = items.filter((p) => p.actif);
  const inactifs = items.filter((p) => !p.actif);
  const visibles = showInactive ? items : actifs;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Plans d'abonnement</h1>
          <p className="text-sm text-slate-400">
            {actifs.length} plan{actifs.length > 1 ? 's' : ''} actif{actifs.length > 1 ? 's' : ''}
            {inactifs.length > 0 && ` · ${inactifs.length} inactif${inactifs.length > 1 ? 's' : ''}`}
          </p>
        </div>
        <button onClick={() => setEditing({})}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <Plus size={16} /> Nouveau plan
        </button>
      </div>

      {inactifs.length > 0 && (
        <button onClick={() => setShowInactive((v) => !v)}
          className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2">
          {showInactive ? 'Masquer les plans inactifs' : `Afficher les plans inactifs (${inactifs.length})`}
        </button>
      )}

      {loading ? (
        <p className="text-slate-400 text-sm">Chargement…</p>
      ) : visibles.length === 0 ? (
        <p className="text-slate-400 text-sm">Aucun plan actif.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {visibles.map((p) => (
            <div key={p.id} className={`bg-white border rounded-xl p-4 ${p.actif ? 'border-slate-200' : 'border-slate-200/40 opacity-60'}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-slate-900 font-medium">{p.nom}</h3>
                    <span className="text-xs text-slate-400 font-mono">{p.code}</span>
                  </div>
                  {p.description && <p className="text-xs text-slate-400 mt-1">{p.description}</p>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${p.actif ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-200 text-slate-400'}`}>
                  {p.actif ? 'actif' : 'inactif'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                <div className="bg-slate-100 rounded-lg px-3 py-2">
                  <p className="text-xs text-slate-400">Mensuel</p>
                  <p className="text-slate-900 font-medium">{fmt(p.prix_mensuel)} GNF</p>
                </div>
                <div className="bg-slate-100 rounded-lg px-3 py-2">
                  <p className="text-xs text-slate-400">Annuel</p>
                  <p className="text-slate-900 font-medium">{fmt(p.prix_annuel)} GNF</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
                <span>{p.max_boutiques_default} boutique{p.max_boutiques_default > 1 ? 's' : ''}</span>
                <span>·</span>
                <span>{p.max_utilisateurs_default} utilisateurs</span>
                <span>·</span>
                <span>{p.nb_abonnements} abonné{p.nb_abonnements > 1 ? 's' : ''}</span>
              </div>

              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(p)}
                  className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-900 text-xs px-3 py-1.5 rounded-lg">
                  <Pencil size={12} /> Modifier
                </button>
                <button onClick={() => toggleActif(p)}
                  title={p.actif ? 'Désactiver' : 'Activer'}
                  className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-900 text-xs px-3 py-1.5 rounded-lg">
                  {p.actif ? <ToggleRight size={12} className="text-emerald-700" /> : <ToggleLeft size={12} />}
                  {p.actif ? 'Désactiver' : 'Activer'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <ModalPlan initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSuccess={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
